import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Modal } from 'react-bootstrap';
import stripeApiService from '../../../services/stripe-subscription.service';
import { IconMapPin, IconX, IconCreditCard } from '@tabler/icons-react';
import PropTypes from 'prop-types';
import './EditBillingAddressModal.css';

const EditBillingAddressModal = ({
  isOpen,
  onClose,
  paymentMethod,
  onAddressUpdated,
  stripeCustomerId,
}) => {
  // Form state
  const [fullName, setFullName] = useState('');
  const [country, setCountry] = useState('US');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');

  // Processing state
  const [processing, setProcessing] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  // Initialize form with payment method data
  useEffect(() => {
    if (isOpen && paymentMethod) {
      const billingDetails = paymentMethod.billing_details || {};
      const address = billingDetails.address || {};

      setFullName(billingDetails.name || '');
      // Ensure country always has a valid value, default to 'US'
      const countryValue = address.country && address.country.trim() !== '' ? address.country : 'US';
      setCountry(countryValue);
      setAddressLine1(address.line1 || '');
      setAddressLine2(address.line2 || '');
      setCity(address.city || '');
      setState(address.state || '');
      setZipCode(address.postal_code || '');
      setFieldErrors({});

      console.log('Initialized form with country:', countryValue);
    }
  }, [isOpen, paymentMethod]);

  // Form validation
  const validateForm = () => {
    const errors = {};

    if (!fullName.trim()) {
      errors.fullName = 'Full name is required';
    }

    if (!addressLine1.trim()) {
      errors.addressLine1 = 'Address line 1 is required';
    }

    if (!city.trim()) {
      errors.city = 'City is required';
    }

    if (!state) {
      errors.state = 'State is required';
    }

    if (!zipCode.trim()) {
      errors.zipCode = 'ZIP code is required';
    }

    if (!country || country.trim() === '') {
      errors.country = 'Country is required';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setProcessing(true);

    try {
      // Ensure country has a valid value
      const countryCode = country && country.trim() !== '' ? country : 'US';

      // Build address object - only include fields with actual values
      const address = {
        line1: addressLine1.trim(),
        city: city.trim(),
        state: state,
        postal_code: zipCode.trim(),
        country: countryCode,
      };

      // Only add line2 if it has a non-empty value
      if (addressLine2 && addressLine2.trim() !== '') {
        address.line2 = addressLine2.trim();
      }

      const billingDetails = {
        name: fullName.trim(),
        address: address,
      };

      console.log('Sending billing details to API:', billingDetails);

      await stripeApiService.updateBillingAddress(
        paymentMethod.id,
        billingDetails
      );

      toast.success('Billing address updated successfully!');
      onAddressUpdated();
      onClose();
    } catch (err) {
      console.error('Update address error:', err);
      toast.error('Failed to update billing address');
    } finally {
      setProcessing(false);
    }
  };

  // US States list
  const US_STATES = [
    { value: '', label: 'Select' },
    { value: 'AL', label: 'Alabama' },
    { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' },
    { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' },
    { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' },
    { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' },
    { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' },
    { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' },
    { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' },
    { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' },
    { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' },
    { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' },
    { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' },
    { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' },
    { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' },
    { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' },
    { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' },
    { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' },
    { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' },
    { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' },
    { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' },
    { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' },
    { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' },
    { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' },
    { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' },
    { value: 'WY', label: 'Wyoming' },
  ];

  const getCardBrandDisplay = (brand) => {
    const brandMap = {
      visa: 'Visa',
      mastercard: 'Mastercard',
      amex: 'American Express',
      discover: 'Discover',
    };
    return brandMap[brand?.toLowerCase()] || brand?.toUpperCase() || 'Card';
  };

  return (
    <>
      <Modal.Header className="edit-address-modal-header">
        <div>
          <Modal.Title as="h3">Edit Billing Address</Modal.Title>
          <p className="modal-subtitle">
            Update billing information for your payment method
          </p>
        </div>
        <button
          className="modal-close-btn"
          onClick={onClose}
          disabled={processing}
        >
          <IconX size={20} />
        </button>
      </Modal.Header>

      <Modal.Body className="edit-address-modal-body">
        {/* Card Info Display */}
        {paymentMethod && (
          <div className="card-info-display">
            <IconCreditCard size={20} />
            <div className="card-info-text">
              <h4>
                {getCardBrandDisplay(paymentMethod.card?.brand)} •••• {paymentMethod.card?.last4}
              </h4>
              <p>
                Expires {String(paymentMethod.card?.exp_month).padStart(2, '0')}/
                {paymentMethod.card?.exp_year}
              </p>
            </div>
          </div>
        )}

        <div className="notice-info">
          <p>
            You are only updating the billing address. Your card information
            will remain unchanged.
          </p>
        </div>

        <form onSubmit={handleSubmit} id="edit-address-form">
          {/* Billing Address Section */}
          <div className="form-section">
            <div className="section-header-small">
              <IconMapPin size={16} />
              <h4>Billing Address</h4>
            </div>

            {/* Full Name */}
            <div className="form-field">
              <label htmlFor="fullName">
                Full name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="fullName"
                className={`form-input ${fieldErrors.fullName ? 'error' : ''}`}
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                disabled={processing}
              />
              {fieldErrors.fullName && (
                <span className="field-error">{fieldErrors.fullName}</span>
              )}
            </div>

            {/* Country */}
            <div className="form-field">
              <label htmlFor="country">Country or region</label>
              <select
                id="country"
                className="form-select"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                disabled={processing}
              >
                <option value="US">United States</option>
              </select>
            </div>

            {/* Address Line 1 */}
            <div className="form-field">
              <label htmlFor="addressLine1">
                Address line 1 <span className="required">*</span>
              </label>
              <input
                type="text"
                id="addressLine1"
                className={`form-input ${fieldErrors.addressLine1 ? 'error' : ''}`}
                value={addressLine1}
                onChange={(e) => setAddressLine1(e.target.value)}
                disabled={processing}
              />
              {fieldErrors.addressLine1 && (
                <span className="field-error">{fieldErrors.addressLine1}</span>
              )}
            </div>

            {/* Address Line 2 */}
            <div className="form-field">
              <label htmlFor="addressLine2">Address line 2</label>
              <input
                type="text"
                id="addressLine2"
                className="form-input"
                placeholder="Apt., suite, unit number, etc. (optional)"
                value={addressLine2}
                onChange={(e) => setAddressLine2(e.target.value)}
                disabled={processing}
              />
            </div>

            {/* City */}
            <div className="form-field">
              <label htmlFor="city">
                City <span className="required">*</span>
              </label>
              <input
                type="text"
                id="city"
                className={`form-input ${fieldErrors.city ? 'error' : ''}`}
                value={city}
                onChange={(e) => setCity(e.target.value)}
                disabled={processing}
              />
              {fieldErrors.city && (
                <span className="field-error">{fieldErrors.city}</span>
              )}
            </div>

            {/* State and ZIP Code */}
            <div className="form-row-2col">
              <div className="form-field">
                <label htmlFor="state">
                  State <span className="required">*</span>
                </label>
                <select
                  id="state"
                  className={`form-select ${fieldErrors.state ? 'error' : ''}`}
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  disabled={processing}
                >
                  {US_STATES.map((st) => (
                    <option key={st.value} value={st.value}>
                      {st.label}
                    </option>
                  ))}
                </select>
                {fieldErrors.state && (
                  <span className="field-error">{fieldErrors.state}</span>
                )}
              </div>

              <div className="form-field">
                <label htmlFor="zipCode">
                  ZIP code <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="zipCode"
                  className={`form-input ${fieldErrors.zipCode ? 'error' : ''}`}
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                  disabled={processing}
                  maxLength={10}
                />
                {fieldErrors.zipCode && (
                  <span className="field-error">{fieldErrors.zipCode}</span>
                )}
              </div>
            </div>
          </div>
        </form>
      </Modal.Body>

      <Modal.Footer className="edit-address-modal-footer">
        <button
          className="btn-cancel"
          onClick={onClose}
          disabled={processing}
        >
          Cancel
        </button>
        <button
          type="submit"
          form="edit-address-form"
          className="btn-update"
          disabled={processing}
        >
          {processing ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              Updating...
            </>
          ) : (
            'Update Billing Address'
          )}
        </button>
      </Modal.Footer>
    </>
  );
};

EditBillingAddressModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  paymentMethod: PropTypes.object,
  onAddressUpdated: PropTypes.func.isRequired,
  stripeCustomerId: PropTypes.string,
};

export default EditBillingAddressModal;
